import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Phone,
  Receipt,
  Percent,
  FileText,
  Check,
  AlertTriangle
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import MobileDatePickerModal from "@/components/common/MobileDatePickerModal";
import MobileTimePickerModal, { formatTime12 } from "@/components/common/MobileTimePickerModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const EVENT_TYPES = [
  "Wedding",
  "Reception",
  "Engagement",
  "Birthday",
  "Corporate",
  "Other"
];

const PRESET_TIMES = {
  day: { start: "06:00", end: "16:59" },
  night: { start: "17:00", end: "23:59" },
  multi_default: { start: "10:00", end: "16:00" }
};

export default function NewBookingPage({ businessInfo }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");

  // 1. Client Details
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");

  // 2. Event Details
  const [eventType, setEventType] = useState("Wedding");
  const [isMultiDay, setIsMultiDay] = useState(false);

  const todayStr = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(dateParam || todayStr);
  const [endDate, setEndDate] = useState(dateParam || todayStr);
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("16:00");
  const [slotType, setSlotType] = useState("day"); // "day" or "night"
  const [guestCount, setGuestCount] = useState("");

  // Modals for Custom Pickers
  const [activeDateModal, setActiveDateModal] = useState(null); // "start" or "end"
  const [activeTimeModal, setActiveTimeModal] = useState(null); // "start" or "end"

  // 3. Financial Details
  const [hallRent, setHallRent] = useState("");
  const [laborCharges, setLaborCharges] = useState("");
  const [electricityCharges, setElectricityCharges] = useState("");
  const [maintenanceFee, setMaintenanceFee] = useState("");
  const [applyGst, setApplyGst] = useState(false);

  const [additionalCharges, setAdditionalCharges] = useState([
    { name: "", amount: "" },
    { name: "", amount: "" },
    { name: "", amount: "" }
  ]);

  const [advancePaid, setAdvancePaid] = useState("");

  // 4. Additional Notes
  const [notes, setNotes] = useState("");

  // Submission & Validation States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (dateParam) {
      setStartDate(dateParam);
      setEndDate(dateParam);
    }
  }, [dateParam]);

  useEffect(() => {
    if (!isMultiDay) {
      setEndDate(startDate);
      if (slotType === "day") {
        setStartTime(PRESET_TIMES.day.start);
        setEndTime(PRESET_TIMES.day.end);
      } else {
        setStartTime(PRESET_TIMES.night.start);
        setEndTime(PRESET_TIMES.night.end);
      }
    } else {
      setStartTime((prev) => prev || "10:00");
      setEndTime((prev) => prev || "16:00");
    }
  }, [isMultiDay, startDate, slotType]);

  // Automatically calculate Financial Totals
  const parsedHallRent = parseFloat(hallRent) > 0 ? parseFloat(hallRent) : 0;
  const parsedLabor = parseFloat(laborCharges) > 0 ? parseFloat(laborCharges) : 0;
  const parsedElectricity = parseFloat(electricityCharges) > 0 ? parseFloat(electricityCharges) : 0;
  const parsedMaintenance = parseFloat(maintenanceFee) > 0 ? parseFloat(maintenanceFee) : 0;

  const sgstAmount = applyGst ? parsedHallRent * 0.09 : 0;
  const cgstAmount = applyGst ? parsedHallRent * 0.09 : 0;
  const totalGstAmount = sgstAmount + cgstAmount;

  const additionalChargesTotal = additionalCharges.reduce((acc, curr) => {
    const amt = parseFloat(curr.amount) > 0 ? parseFloat(curr.amount) : 0;
    return acc + amt;
  }, 0);

  const totalAmount =
    parsedHallRent +
    parsedLabor +
    parsedElectricity +
    parsedMaintenance +
    totalGstAmount +
    additionalChargesTotal;

  const parsedAdvance = parseFloat(advancePaid) > 0 ? parseFloat(advancePaid) : 0;
  const balanceDue = Math.max(0, totalAmount - parsedAdvance);

  const handleAdditionalChargeChange = (index, field, value) => {
    const updated = [...additionalCharges];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalCharges(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Validate Client Name
    if (!clientName.trim()) {
      setFormError("Client Name is required.");
      return;
    }

    // Validate Phone (10 digits required)
    const cleanPhone = clientPhone.replace(/\D/g, "");
    if (cleanPhone.length !== 10) {
      setFormError("Please enter a valid 10-digit mobile phone number.");
      return;
    }

    // Validate Dates
    if (!startDate) {
      setFormError("Start Date is required.");
      return;
    }
    if (isMultiDay && (!endDate || endDate < startDate)) {
      setFormError("End Date must be on or after Start Date.");
      return;
    }

    const effectiveSlot = isMultiDay ? "full_day" : slotType;
    const finalStartTime = isMultiDay ? (startTime || "10:00") : (slotType === "day" ? PRESET_TIMES.day.start : PRESET_TIMES.night.start);
    const finalEndTime = isMultiDay ? (endTime || "16:00") : (slotType === "day" ? PRESET_TIMES.day.end : PRESET_TIMES.night.end);
    const finalEndDate = isMultiDay ? endDate : startDate;
    const startIso = `${startDate}T${finalStartTime}:00+00:00`;
    const endIso = `${finalEndDate}T${finalEndTime}:00+00:00`;

    setIsSubmitting(true);
    try {
      const filteredAddCharges = additionalCharges
        .filter((c) => c.name.trim() !== "" || parseFloat(c.amount) > 0)
        .map((c) => ({
          name: c.name.trim() || "Extra Service",
          amount: parseFloat(c.amount) || 0
        }));

      const payload = {
        clientName: clientName.trim(),
        customer_name: clientName.trim(),
        client_name: clientName.trim(),
        phone: cleanPhone,
        customer_phone: cleanPhone,
        client_phone: cleanPhone,
        event_type: eventType,
        booking_type: isMultiDay ? "multi_day" : "single_day",
        slot_type: effectiveSlot,
        eventDate: startDate,
        date: startDate,
        start_date: startDate,
        endDate: finalEndDate,
        end_date: finalEndDate,
        start_time: finalStartTime,
        end_time: finalEndTime,
        start_datetime_iso: startIso,
        end_datetime_iso: endIso,
        guest_count: guestCount ? parseInt(guestCount) : null,
        // Financial Details
        hall_rent: parsedHallRent,
        hallRent: parsedHallRent,
        labor_charges: parsedLabor,
        laborCharges: parsedLabor,
        electricity_charges: parsedElectricity,
        electricityCharges: parsedElectricity,
        maintenance_fee: parsedMaintenance,
        maintenanceFee: parsedMaintenance,
        apply_gst: applyGst,
        applyGst: applyGst,
        sgst_amount: sgstAmount,
        sgstAmount: sgstAmount,
        cgst_amount: cgstAmount,
        cgstAmount: cgstAmount,
        additional_charges: filteredAddCharges,
        totalAmount: totalAmount,
        total_rent: totalAmount,
        total_amount: totalAmount,
        advancePaid: parsedAdvance,
        deposit_amount: parsedAdvance,
        advance_paid: parsedAdvance,
        balanceDue: balanceDue,
        balance_due: balanceDue,
        booking_status: "confirmed",
        status: "confirmed",
        notes: notes.trim()
      };

      const res = await axios.post(`${API}/bookings`, payload);
      navigate(`/bookings/${res.data.id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to create booking. Please check date availability.";
      setFormError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto pb-12">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/bookings"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bookings</span>
        </Link>
        <span className="text-xs font-semibold text-pink-900 bg-pink-50 px-3 py-1 rounded-full border border-pink-200">
          Mobile App Form
        </span>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            New Event Booking
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1">
            Fill in the details below to create a booking synchronized with the mobile app.
          </p>
        </div>

        {formError && (
          <div className="m-6 p-4 rounded-2xl bg-rose-50 border border-rose-300 text-rose-950 text-xs font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
            <span>{formError}</span>
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-8 bg-gray-50/20">
          
          {/* =========================================================
              SECTION 1: CLIENT DETAILS
             ========================================================= */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-pink-100 pb-3">
              <User className="w-4 h-4 text-pink-700" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
                Client Details
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Client Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Client Name <span className="text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  data-testid="input-client-name"
                  required
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. Ramesh Kumar"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-xs text-gray-900 bg-white"
                />
              </div>

              {/* Phone (10 digits) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Phone (10 Digits) <span className="text-rose-600">*</span>
                </label>
                <div>
                  <input
                    type="tel"
                    data-testid="input-client-phone"
                    required
                    maxLength={10}
                    value={clientPhone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 10) setClientPhone(val);
                    }}
                    placeholder="9845012345"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-xs font-mono text-gray-900 bg-white"
                  />
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    Enter 10 digit mobile number without +91
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* =========================================================
              SECTION 2: EVENT DETAILS
             ========================================================= */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-pink-100 shadow-sm space-y-5">
            <div className="flex items-center gap-2 border-b border-pink-100 pb-3">
              <Calendar className="w-4 h-4 text-pink-700" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
                Event Details
              </h3>
            </div>

            {/* Event Type choices */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">
                Event Type Choice
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {EVENT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    data-testid={`event-type-${type.toLowerCase()}`}
                    onClick={() => setEventType(type)}
                    className={`px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-center border ${
                      eventType === type
                        ? "bg-pink-900 border-pink-900 text-white shadow-sm"
                        : "bg-pink-50/50 border-pink-100 text-pink-900 hover:bg-pink-100"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Multi-Day Booking Switch */}
            <div className="flex items-center justify-between p-3.5 rounded-2xl bg-pink-50/60 border border-pink-100">
              <div>
                <span className="text-xs font-bold text-pink-950 block">Multi-Day Booking</span>
                <span className="text-[11px] text-gray-500">Enable for events spanning multiple consecutive dates & times</span>
              </div>
              <button
                type="button"
                data-testid="toggle-multi-day"
                onClick={() => setIsMultiDay(!isMultiDay)}
                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${
                  isMultiDay ? "bg-pink-900" : "bg-gray-300"
                }`}
              >
                <div
                  className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                    isMultiDay ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Event Date / Dates Configuration */}
            {!isMultiDay ? (
              /* Single-Day: Event Date + Function Choice */
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Event Date <span className="text-rose-600">*</span>
                  </label>
                  <button
                    type="button"
                    data-testid="btn-open-start-date"
                    onClick={() => setActiveDateModal("start")}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 bg-white hover:bg-pink-50/40 text-left text-xs font-bold text-pink-950 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-pink-700" />
                      <span>{startDate}</span>
                    </span>
                    <span className="text-[11px] text-pink-700 font-semibold underline">Change Date</span>
                  </button>
                </div>

                {/* Day Function or Night Function Choice */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Function Time Choice
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      data-testid="slot-day"
                      onClick={() => setSlotType("day")}
                      className={`p-3 rounded-xl border text-left text-xs transition-all ${
                        slotType === "day"
                          ? "bg-pink-900 border-pink-900 text-white font-bold shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-pink-200"
                      }`}
                    >
                      <div className="font-bold">Day Function</div>
                      <div className={`text-[11px] ${slotType === "day" ? "text-pink-100" : "text-gray-500"}`}>
                        06:00 AM - 04:59 PM
                      </div>
                    </button>

                    <button
                      type="button"
                      data-testid="slot-night"
                      onClick={() => setSlotType("night")}
                      className={`p-3 rounded-xl border text-left text-xs transition-all ${
                        slotType === "night"
                          ? "bg-pink-900 border-pink-900 text-white font-bold shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-pink-200"
                      }`}
                    >
                      <div className="font-bold">Night Function</div>
                      <div className={`text-[11px] ${slotType === "night" ? "text-pink-100" : "text-gray-500"}`}>
                        05:00 PM - 11:59 PM
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Multi-Day: Exactly 4 ordered fields: 1. Start Date, 2. Start Time, 3. End Date, 4. End Time */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-2xl bg-pink-50/40 border border-pink-100">
                {/* 1. Start Date (required) */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Start Date <span className="text-rose-600">*</span>
                  </label>
                  <button
                    type="button"
                    data-testid="btn-open-start-date"
                    onClick={() => setActiveDateModal("start")}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 bg-white hover:bg-pink-50/50 text-left text-xs font-bold text-pink-950 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-pink-700" />
                      <span>{startDate}</span>
                    </span>
                    <span className="text-[11px] text-pink-700 font-semibold underline">Select</span>
                  </button>
                </div>

                {/* 2. Start Time (required, default 10:00 AM) */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Start Time <span className="text-rose-600">*</span>
                  </label>
                  <button
                    type="button"
                    data-testid="btn-open-start-time"
                    onClick={() => setActiveTimeModal("start")}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 bg-white hover:bg-pink-50/50 text-left text-xs font-bold text-pink-950 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-pink-700" />
                      <span>{formatTime12(startTime || "10:00")}</span>
                    </span>
                    <span className="text-[11px] text-pink-700 font-semibold underline">Pick Time</span>
                  </button>
                </div>

                {/* 3. End Date (required) */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    End Date <span className="text-rose-600">*</span>
                  </label>
                  <button
                    type="button"
                    data-testid="btn-open-end-date"
                    onClick={() => setActiveDateModal("end")}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 bg-white hover:bg-pink-50/50 text-left text-xs font-bold text-pink-950 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-pink-700" />
                      <span>{endDate}</span>
                    </span>
                    <span className="text-[11px] text-pink-700 font-semibold underline">Select</span>
                  </button>
                </div>

                {/* 4. End Time (required, default 4:00 PM) */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    End Time <span className="text-rose-600">*</span>
                  </label>
                  <button
                    type="button"
                    data-testid="btn-open-end-time"
                    onClick={() => setActiveTimeModal("end")}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 bg-white hover:bg-pink-50/50 text-left text-xs font-bold text-pink-950 flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-pink-700" />
                      <span>{formatTime12(endTime || "16:00")}</span>
                    </span>
                    <span className="text-[11px] text-pink-700 font-semibold underline">Pick Time</span>
                  </button>
                </div>
              </div>
            )}

            {/* Expected Guest Count */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Expected Guest Count
              </label>
              <input
                type="number"
                data-testid="input-guest-count"
                placeholder="e.g. 400"
                value={guestCount}
                onChange={(e) => setGuestCount(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-xs text-gray-900 bg-white"
              />
            </div>
          </div>

          {/* =========================================================
              SECTION 3: FINANCIAL DETAILS
             ========================================================= */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-pink-100 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-pink-100 pb-3">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-pink-700" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
                  Financial Details
                </h3>
              </div>
            </div>

            {/* Four primary charges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <label className="block text-gray-700 font-semibold mb-1">Hall Rent (₹)</label>
                <input
                  data-testid="input-hall-rent"
                  type="number"
                  placeholder="0"
                  value={hallRent}
                  onChange={(e) => setHallRent(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-xs font-bold text-pink-950 focus:outline-none focus:ring-2 focus:ring-pink-700 bg-white"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-1">Labor Charges (₹)</label>
                <input
                  data-testid="input-labor-charges"
                  type="number"
                  placeholder="0"
                  value={laborCharges}
                  onChange={(e) => setLaborCharges(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-xs focus:outline-none focus:ring-2 focus:ring-pink-700 bg-white"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-1">Electricity Charges (₹)</label>
                <input
                  data-testid="input-electricity-charges"
                  type="number"
                  placeholder="0"
                  value={electricityCharges}
                  onChange={(e) => setElectricityCharges(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-xs focus:outline-none focus:ring-2 focus:ring-pink-700 bg-white"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-1">Maintenance Fee (₹)</label>
                <input
                  data-testid="input-maintenance-fee"
                  type="number"
                  placeholder="0"
                  value={maintenanceFee}
                  onChange={(e) => setMaintenanceFee(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-xs focus:outline-none focus:ring-2 focus:ring-pink-700 bg-white"
                />
              </div>
            </div>

            {/* Apply 18% GST Switch */}
            <div className="p-3.5 rounded-2xl bg-pink-50/70 border border-pink-200 flex items-center justify-between transition-all">
              <div>
                <div className="text-xs font-bold text-pink-950 flex items-center gap-1.5">
                  <Percent className="w-3.5 h-3.5 text-pink-700" />
                  <span>Apply 18% GST (9% SGST + 9% CGST on Hall Rent only)</span>
                </div>
                <div className="text-[11px] text-pink-800 mt-0.5">
                  {applyGst ? (
                    <span>SGST (9%): ₹{Number(sgstAmount).toLocaleString("en-IN")} + CGST (9%): ₹{Number(cgstAmount).toLocaleString("en-IN")} = ₹{Number(totalGstAmount).toLocaleString("en-IN")}</span>
                  ) : (
                    <span className="text-gray-500">Toggle switch to apply 18% GST on base hall rent</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                data-testid="toggle-apply-gst"
                onClick={() => setApplyGst(!applyGst)}
                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${
                  applyGst ? "bg-pink-900" : "bg-gray-300"
                }`}
              >
                <div
                  className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                    applyGst ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Additional Charges (3 rows, optional) */}
            <div className="space-y-2 pt-1">
              <label className="block text-xs font-bold text-gray-700">
                Additional Charges (Optional - 3 Rows)
              </label>
              <div className="space-y-2">
                {additionalCharges.map((charge, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      data-testid={`input-add-charge-name-${idx}`}
                      placeholder={`Charge Name #${idx + 1} (e.g. Mandap)`}
                      value={charge.name}
                      onChange={(e) => handleAdditionalChargeChange(idx, "name", e.target.value)}
                      className="col-span-2 px-3.5 py-2 rounded-xl border border-pink-200 text-xs bg-white"
                    />
                    <input
                      type="number"
                      data-testid={`input-add-charge-amt-${idx}`}
                      placeholder="Amount (₹)"
                      value={charge.amount}
                      onChange={(e) => handleAdditionalChargeChange(idx, "amount", e.target.value)}
                      className="px-3.5 py-2 rounded-xl border border-pink-200 text-xs bg-white"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Summary: Total Amount, Advance Paid, Balance Due */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 border-t border-pink-100">
              {/* Total Amount (Automatic) */}
              <div className="p-4 rounded-2xl bg-pink-100/70 border border-pink-300 text-center">
                <span className="text-xs text-gray-700 font-bold block">Total Amount</span>
                <span className="text-xl font-black text-pink-950 font-mono mt-0.5 block">
                  ₹{Number(totalAmount).toLocaleString("en-IN")}
                </span>
                <span className="text-[10px] text-gray-500 block mt-0.5">Calculated automatically</span>
              </div>

              {/* Advance Paid */}
              <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-center">
                <label className="text-xs text-emerald-900 font-bold block mb-1">Advance Paid (₹)</label>
                <input
                  data-testid="input-advance-paid"
                  type="number"
                  placeholder="0"
                  value={advancePaid}
                  onChange={(e) => setAdvancePaid(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-center font-mono font-bold text-emerald-950 bg-white rounded-xl border border-emerald-300 text-base focus:outline-none focus:ring-2 focus:ring-emerald-600"
                />
              </div>

              {/* Balance Due (Automatic) */}
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-center">
                <span className="text-xs text-rose-900 font-bold block">Balance Due</span>
                <span className="text-xl font-black text-rose-900 font-mono mt-0.5 block">
                  ₹{Number(balanceDue).toLocaleString("en-IN")}
                </span>
                <span className="text-[10px] text-gray-500 block mt-0.5">Calculated automatically</span>
              </div>
            </div>
          </div>

          {/* =========================================================
              SECTION 4: ADDITIONAL NOTES
             ========================================================= */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-pink-100 shadow-sm space-y-3">
            <div className="flex items-center gap-2 border-b border-pink-100 pb-3">
              <FileText className="w-4 h-4 text-pink-700" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
                Additional Notes
              </h3>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Notes / Special Requirements
              </label>
              <textarea
                rows={3}
                placeholder="Enter any client specifications, mandap setup requests, catering arrangements..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-xs text-gray-900 bg-white"
              />
            </div>
          </div>

          {/* =========================================================
              CONFIRM BOOKING BUTTON
             ========================================================= */}
          <div className="pt-2">
            <button
              type="submit"
              data-testid={TEST_IDS.btnConfirmBooking}
              disabled={isSubmitting}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 text-white font-black text-sm uppercase tracking-wider shadow-xl hover:opacity-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2 active:scale-[0.99]"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving Booking...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Confirm Booking</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Custom Mobile Date Picker Modal */}
      <MobileDatePickerModal
        isOpen={activeDateModal !== null}
        onClose={() => setActiveDateModal(null)}
        selectedDate={activeDateModal === "start" ? startDate : endDate}
        onSelectDate={(newDate) => {
          if (activeDateModal === "start") {
            setStartDate(newDate);
            if (!isMultiDay) setEndDate(newDate);
          } else {
            setEndDate(newDate);
          }
        }}
        title={activeDateModal === "start" ? (isMultiDay ? "Select Start Date" : "Select Event Date") : "Select End Date"}
      />

      {/* Custom Mobile Time Picker Modal */}
      <MobileTimePickerModal
        isOpen={activeTimeModal !== null}
        onClose={() => setActiveTimeModal(null)}
        initialTime={activeTimeModal === "start" ? (startTime || "10:00") : (endTime || "16:00")}
        onSelectTime={(newTime) => {
          if (activeTimeModal === "start") {
            setStartTime(newTime);
          } else {
            setEndTime(newTime);
          }
        }}
        title={activeTimeModal === "start" ? "Select Start Time" : "Select End Time"}
      />
    </div>
  );
}
